import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Send, CheckCircle } from '@mui/icons-material';
import axios from 'axios';

const EmailTesting: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSendTest = async () => {
    setLoading(true);
    setSuccess(null);
    setError(null);

    try {
      const response = await axios.post('https://hst-gst-tracker.fareedsheikllp.com/api/test-email', {
        email: email,
      });

      setSuccess(response.data.message);
      setError(null);
    } catch (err: any) {
      console.error('Error:', err);
      setError(err.response?.data?.message || 'Failed to send test email');
      setSuccess(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#1a1a1a' }}>
        Email Testing
      </Typography>
      <Typography sx={{ color: '#666666', fontSize: '1rem', mb: 4 }}>
        Send a test email to verify your Resend configuration
      </Typography>

      {success && (
        <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4, maxWidth: 600, border: '1px solid #E5E7EB' }} elevation={0}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
          Send Test Email
        </Typography>

        <TextField
          fullWidth
          label="Your Email Address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your.email@example.com"
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#F9FAFB',
              '& fieldset': { borderColor: '#E5E7EB' },
              '&:hover fieldset': { borderColor: '#667eea' },
              '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
            },
          }}
        />

        <Button
          fullWidth
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
          onClick={handleSendTest}
          disabled={loading || !email}
          sx={{
            textTransform: 'none',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#FFFFFF',
            fontWeight: 600,
            py: 1.5,
            fontSize: '1rem',
            '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)' },
            '&:disabled': { background: '#E5E7EB', color: '#9CA3AF' },
          }}
        >
          {loading ? 'Sending...' : 'Send Test Email'}
        </Button>
      </Paper>

      <Paper sx={{ p: 4, mt: 3, backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }} elevation={0}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Configuration Status
        </Typography>
        <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
          Make sure your <code style={{ backgroundColor: '#E5E7EB', padding: '2px 6px', borderRadius: '4px' }}>backend/.env</code> file contains:
        </Typography>
        <Box
          component="pre"
          sx={{
            backgroundColor: '#1E293B',
            color: '#E2E8F0',
            p: 2,
            borderRadius: 1,
            fontSize: '0.875rem',
            overflow: 'auto',
          }}
        >
{`RESEND_API_KEY=re_xxxxxxxxxxxxx`}
        </Box>
        <Typography variant="caption" sx={{ color: '#6B7280', mt: 2, display: 'block' }}>
          📧 Get your API key at{' '}
          <a href="https://resend.com" target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
            resend.com
          </a>
        </Typography>
      </Paper>
    </Box>
  );
};

export default EmailTesting;